import { queryOptions } from '@tanstack/react-query';

import { buildURL, patch, post } from '@/shared/fetch';

import { getQueryFn } from '../utils';
import {
  GetProductPairParams,
  GetProductPairResponse,
  GetProductsParams,
  GetProductsResponse,
  GetUserProductsParams,
  GetUserProductsResponse,
  GetUserRetentionOffersResponse,
  PatchUserProductParams,
  PatchUserProductResponse,
  PostUserProductRequest,
  PostUserProductResponse,
  PostUserRetentionOfferRequest,
  PostUserRetentionOfferResponse,
} from './types';

/**
 * The prefix every products query shares, for invalidating the lot of them at once.
 *
 * There is one list per set of params — each card asks for its own `lookupKey` — and a session
 * change makes every one of them wrong at the same moment, so the answer is always the whole
 * prefix rather than any single key.
 */
export const productsQueryKeyPrefix = ['products'] as const;

export const getProductsQueryOptions = (params?: GetProductsParams) =>
  queryOptions({
    queryKey: [...productsQueryKeyPrefix, 'list', params],
    queryFn: getQueryFn<GetProductsResponse>('/v2/products', params),
    // A minute, as the educators app has by default. Without it a card refetches the list the
    // product pair has just filed for it (`useProductPairSync`), and the flow is back to one
    // request per card. A purchase changes the pair's key, so the next pair re-files fresh lists.
    staleTime: 60 * 1000,
  });

/**
 * The Speedy Flow's pair of cards, chosen by the API from the link, the gate and the visitor.
 *
 * Keyed on `visitor` — who is asking and what they hold — as well as on the question, so a
 * purchase, a lapse or a sign-in simply asks a different key rather than anything having to clear
 * a cache. Otherwise it never goes stale: the same visitor opening the flow for the same habitat
 * again gets the answer they already have, with no request.
 */
export const getProductPairQueryOptions = (params: GetProductPairParams, visitor: string) =>
  queryOptions({
    queryKey: [...productsQueryKeyPrefix, 'pair', visitor, params],
    queryFn: getQueryFn<GetProductPairResponse>('/v2/products/pair', params),
    staleTime: Infinity,
  });

export const getUserProductsQueryOptions = (params?: GetUserProductsParams) =>
  queryOptions({
    queryKey: ['user-products', 'list', params],
    queryFn: getQueryFn<GetUserProductsResponse>('/v2/users/products', params),
  });

export const patchUserProductMutationFn = async ({ userProductId, ...params }: PatchUserProductParams) => {
  const response = await patch<PatchUserProductResponse>(buildURL(`/v2/users/products/${userProductId}`), params);
  if (response) {
    // extend the response to 2 seconds to wait for the new product webhook to be processed
    return new Promise((resolve) => setTimeout(() => resolve(response), 2000));
  }
  return response;
};

export const getUserRetentionOffersQueryOptions = (userProductId: string) =>
  queryOptions({
    queryKey: ['retention-offers', 'list', userProductId],
    queryFn: getQueryFn<GetUserRetentionOffersResponse>(`/v2/users/products/${userProductId}/retentionOffers`),
  });

export const postUserProductMutationFn = async (args: PostUserProductRequest) => {
  return post<PostUserProductResponse>(buildURL('/v2/users/products'), args);
};

export const postUserRetentionOfferMutationFn = async ({ userProductId, ...args }: PostUserRetentionOfferRequest) => {
  return post<PostUserRetentionOfferResponse>(buildURL(`/v2/users/products/${userProductId}/retentionOffers`), args);
};
